import { Field, Image, ImageField, LinkField } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { sanitize } from 'isomorphic-dompurify';

import {
  DynamicHeading,
  getContrastThemeClass,
  QueryParamsFields,
  resolveBackgroundStyle,
  resolveColorProperty,
  useBreakpoint,
  useShouldRenderForQueryParam,
} from '@gn-audio/jss-components';

import NavButton from 'composables/shared/NavButton/NavButton';
import { useSpotTracking } from 'utils/hooks/useSpotTracking';

type TopBannerSize = 'small' | 'medium' | 'large';
type logoPosition = 'first' | 'last';

export type TopBannerFields = {
  heading: Field<string>;
  subHeading?: Field<string>;
  eyebrow: Field<string>;
  description: Field<string>;
  size: Field<TopBannerSize>;
  forceDarkTheme: Field<boolean>;
  backgroundColor: Field<string>;
  backgroundImageDesktop: ImageField;
  backgroundImageTablet: ImageField;
  backgroundImageMobile: ImageField;
  imageBackgroundColor?: Field<string>;
  logo: ImageField;
  logoPosition: Field<logoPosition>;
  primaryButtonText: Field<string>;
  primaryButtonLink: LinkField;
  primaryButtonAriaLabel?: Field<string>;
  secondaryButtonText: Field<string>;
  secondaryButtonLink: LinkField;
  secondaryButtonAriaLabel?: Field<string>;
  primaryButtonIcon?: Field<string>;
  secondaryButtonIcon?: Field<string>;
  isMainHeading: Field<boolean>;
  fullWidthMode: Field<boolean>;
  disableGradient: Field<boolean>;
  mainHeadingColor: Field<string>;
  secondaryHeadingColor: Field<string>;
  descriptionColor: Field<string>;
  eyebrowColor: Field<string>;
  firstCtaButtonTrackingName?: Field<string>;
  secondCtaButtonTrackingName?: Field<string>;
  trackingSpotName?: Field<string>;
};

export type TopBannerItemProps = {
  fields: TopBannerFields & QueryParamsFields;
};

const imageSizes: { [key in TopBannerSize]: { h: number; w: number } } = {
  small: {
    h: 420,
    w: 708,
  },
  medium: {
    h: 640,
    w: 708,
  },
  large: {
    h: 900,
    w: 920,
  },
};

type classNamesBySize = Partial<{ [key in TopBannerSize]: string }>;

const secondaryTitleClassNames: classNamesBySize = {
  small: 'text-base',
  medium: 'text-base',
  large: 'text-xl',
};

const buttonContainerClassNames: classNamesBySize = {
  small: 'pt-8',
  medium: 'pt-8',
  large: 'pt-10',
};

export const TopBannerItem = ({ fields }: TopBannerItemProps) => {
  const { isBelowMd } = useBreakpoint('md', true);

  const size = fields.size?.value || 'large';
  const buttonSize = size !== 'small' || isBelowMd ? 'l' : 'm';
  const {
    primaryButtonText,
    primaryButtonLink,
    primaryButtonAriaLabel,
    secondaryButtonLink,
    secondaryButtonText,
    secondaryButtonAriaLabel,
    forceDarkTheme,
    backgroundImageDesktop,
    backgroundImageTablet,
    backgroundImageMobile,
    backgroundColor,
    imageBackgroundColor,
    logo,
    logoPosition,
    eyebrow,
    heading,
    subHeading,
    description,
    primaryButtonIcon,
    secondaryButtonIcon,
    isMainHeading,
    fullWidthMode,
    disableGradient,
    mainHeadingColor,
    secondaryHeadingColor,
    descriptionColor,
    eyebrowColor,
    firstCtaButtonTrackingName,
    secondCtaButtonTrackingName,
    trackingSpotName,
    queryParamKey,
    queryParamValue,
    queryParamAction,
  } = fields;

  const spotRef = useSpotTracking(trackingSpotName?.value);
  const shouldRender = useShouldRenderForQueryParam({
    queryParamKey,
    queryParamValue,
    queryParamAction,
  });
  if (!shouldRender) return null;

  const SubHeadingTag: React.ElementType = isMainHeading?.value ? 'h2' : 'h3';
  const doesHavePrimaryButton = primaryButtonText?.value && !!primaryButtonLink?.value?.href;
  const doesHaveSecondaryButton = secondaryButtonText?.value && !!secondaryButtonLink?.value?.href;
  const spotThemeClass = getContrastThemeClass(backgroundColor.value as string);
  const spotBackgroundStyle = resolveBackgroundStyle(backgroundColor.value as string);
  const imageBackgroundStyle = resolveBackgroundStyle(
    imageBackgroundColor?.value || backgroundColor.value || 'transparent'
  );
  const coverMode = fullWidthMode?.value || false;
  const forceDarkThemeClass = forceDarkTheme?.value ? 'dark' : '';

  const mainHeadingColorStyle = resolveColorProperty(mainHeadingColor?.value as string);
  const secondaryHeadingColorStyle = resolveColorProperty(secondaryHeadingColor?.value as string);
  const descriptionColorStyle = resolveColorProperty(descriptionColor?.value as string);
  const eyebrowColorStyle = resolveColorProperty(eyebrowColor?.value as string);

  return (
    <div
      ref={spotRef}
      data-wt-spot={trackingSpotName?.value}
      className={classNames('relative flex w-full justify-end', spotThemeClass)}
      style={spotBackgroundStyle}
    >
      <div
        className={classNames(
          'container-fluid left-0 right-0 z-20 m-auto flex h-full w-full max-xl:flex-col xl:absolute',
          coverMode ? 'items-end xl:p-24' : 'xl:container xl:items-center'
        )}
      >
        {(backgroundImageMobile?.value?.src ||
          backgroundImageTablet?.value?.src ||
          backgroundImageDesktop?.value?.src) && (
          <div className="container-left flex-shrink-0 py-0 xl:hidden">
            <div
              style={imageBackgroundStyle}
              className="size-full h-[unset] max-h-[550px] overflow-hidden rounded-l-full max-sm:max-h-[400px]"
            >
              <picture>
                {backgroundImageMobile?.value?.src && (
                  <source media="(max-width: 767px)" srcSet={backgroundImageMobile.value.src} />
                )}
                {backgroundImageTablet?.value?.src && (
                  <source
                    media="(min-width: 768px) and (max-width: 1279px)"
                    srcSet={backgroundImageTablet.value.src}
                  />
                )}
                <img
                  src={
                    backgroundImageMobile?.value?.src ||
                    backgroundImageTablet?.value?.src ||
                    backgroundImageDesktop?.value?.src ||
                    ''
                  }
                  alt={
                    (backgroundImageMobile?.value?.alt ||
                      backgroundImageTablet?.value?.alt ||
                      backgroundImageDesktop?.value?.alt ||
                      '') as string
                  }
                  className="size-full h-[unset] max-h-[550px] object-cover object-center max-sm:max-h-[400px]"
                  height={imageSizes[size]?.h}
                  width={imageSizes[size]?.w}
                />
              </picture>
            </div>
          </div>
        )}
        <div
          className={classNames(
            forceDarkThemeClass,
            'basis-1/2 pb-16 pt-8 max-xl:container md:pr-6'
          )}
        >
          <div>
            {logoPosition?.value !== 'last' && logo?.value?.src && (
              <Image
                field={{
                  src: logo.value?.src,
                  alt: logo.value?.alt,
                }}
                className="mb-6 max-h-14 w-auto"
                width="100%"
              />
            )}
            {eyebrow.value && (
              <>
                <span
                  style={{ color: eyebrowColorStyle }}
                  className={classNames(
                    secondaryTitleClassNames[size],
                    'paragraph mb-0 block pb-2 uppercase'
                  )}
                >
                  {eyebrow.value}
                </span>
                <div className="mb-2 h-0.5 w-24 bg-accent" />
              </>
            )}
            <DynamicHeading
              style={{ color: mainHeadingColorStyle }}
              className={classNames('heading-1')}
              isMainHeading={isMainHeading.value}
              htmlContent={sanitize(heading?.value)}
            />
            {subHeading?.value && (
              <SubHeadingTag
                style={{ color: secondaryHeadingColorStyle }}
                className="heading-4"
                dangerouslySetInnerHTML={{
                  __html: sanitize(subHeading.value),
                }}
              />
            )}
            {description?.value && (
              <p
                style={{ color: descriptionColorStyle }}
                className={classNames('paragraph-large')}
                dangerouslySetInnerHTML={{
                  __html: sanitize(description.value),
                }}
              />
            )}
            {logoPosition?.value === 'last' && logo?.value?.src && (
              <Image
                field={{
                  src: logo.value?.src,
                  alt: logo.value?.alt,
                }}
                className="mt-6 max-h-14 w-auto"
                width="100%"
              />
            )}
            <div className={classNames(buttonContainerClassNames[size], 'flex flex-wrap gap-6')}>
              {doesHavePrimaryButton && (
                <NavButton
                  variant="primary"
                  size={buttonSize}
                  link={primaryButtonLink}
                  title={primaryButtonText?.value}
                  icon={primaryButtonIcon?.value}
                  ariaLabel={primaryButtonAriaLabel?.value}
                  {...(firstCtaButtonTrackingName?.value && {
                    'data-wt-name': firstCtaButtonTrackingName.value,
                  })}
                />
              )}
              {doesHaveSecondaryButton && (
                <NavButton
                  variant="secondary"
                  size={buttonSize}
                  link={secondaryButtonLink}
                  title={secondaryButtonText?.value}
                  icon={secondaryButtonIcon?.value}
                  ariaLabel={secondaryButtonAriaLabel?.value}
                  {...(secondCtaButtonTrackingName?.value && {
                    'data-wt-name': secondCtaButtonTrackingName.value,
                  })}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <div
        style={{ height: `${imageSizes[size]?.h}px`, ...imageBackgroundStyle }}
        className={classNames(
          'max-xl:hidden',
          coverMode ? 'relative flex-1' : 'basis-1/2 overflow-hidden rounded-l-full'
        )}
      >
        {(backgroundImageMobile?.value?.src ||
          backgroundImageTablet?.value?.src ||
          backgroundImageDesktop?.value?.src) && (
          <img
            src={
              backgroundImageDesktop?.value?.src ||
              backgroundImageTablet?.value?.src ||
              backgroundImageMobile?.value?.src ||
              ''
            }
            alt={
              (backgroundImageDesktop?.value?.alt ||
                backgroundImageTablet?.value?.alt ||
                backgroundImageMobile?.value?.alt ||
                '') as string
            }
            className="size-full object-cover"
            height={imageSizes[size]?.h}
            width={imageSizes[size]?.w}
          />
        )}
        {coverMode && !disableGradient?.value && (
          <div
            className="absolute inset-0 z-10"
            style={{
              background: 'linear-gradient(90deg, rgba(0, 0, 0, 0.58) 18%, rgba(0, 0, 0, 0) 100%)',
            }}
          />
        )}
      </div>
    </div>
  );
};
